import CampoTexto from '@/components/base/CampoTexto';
import { UFS } from '@/pages/meu-perfil/types';
import type { PessoaForm } from '../types';

interface CartaoPessoaProps {
  form: PessoaForm;
  modo: 'eu' | 'outra';
  mostrarEndereco: boolean;
  erros: Record<string, string>;
  onChange: (campo: keyof PessoaForm, valor: string) => void;
}

export default function CartaoPessoa({
  form,
  modo,
  mostrarEndereco,
  erros,
  onChange,
}: CartaoPessoaProps) {
  return (
    <div className="rounded-lg border border-secondary-200 bg-white p-6">
      {modo === 'eu' && (
        <p className="mb-4 text-sm text-secondary-500">
          Confira seus dados. O que estiver faltando, complete.
        </p>
      )}

      <div className="space-y-6">
        <CampoTexto
          id="nome"
          name="nome"
          label="Nome completo"
          help="Nome de quem vai participar do evento, como está no documento."
          value={form.nome}
          onChange={(v) => onChange('nome', v)}
          required
          error={erros.nome}
          autoComplete="name"
        />

        <CampoTexto
          id="cpf"
          name="cpf"
          label="CPF"
          help="Opcional. Ajuda a organização a identificar a pessoa na chegada."
          value={form.cpf}
          onChange={(v) => onChange('cpf', v)}
          error={erros.cpf}
          inputMode="numeric"
          placeholder="000.000.000-00"
          autoComplete="off"
        />

        <CampoTexto
          id="nascimento"
          name="nascimento"
          label="Data de nascimento"
          help="Opcional. Usada para saber a idade no dia do evento."
          value={form.nascimento}
          onChange={(v) => onChange('nascimento', v)}
          error={erros.nascimento}
          inputMode="numeric"
          placeholder="DD/MM/AAAA"
          autoComplete="bday"
        />

        <CampoTexto
          id="telefone"
          name="telefone"
          label="Telefone"
          help="Com DDD. É por aqui que a organização fala com o participante."
          value={form.telefone}
          onChange={(v) => onChange('telefone', v)}
          required
          error={erros.telefone}
          inputMode="tel"
          placeholder="(00) 00000-0000"
          autoComplete="tel"
        />

        <CampoTexto
          id="email"
          name="email"
          label="E-mail"
          help="Opcional. Se preencher, enviamos a confirmação da inscrição também para este endereço."
          value={form.email}
          onChange={(v) => onChange('email', v)}
          error={erros.email}
          type="email"
          inputMode="email"
          placeholder="maria@gmail.com"
          autoComplete="off"
        />

        {mostrarEndereco && (
          <>
            <CampoTexto
              id="cep"
              name="cep"
              label="CEP"
              help="Opcional. Ao digitar, buscamos o endereço sozinhos."
              value={form.cep}
              onChange={(v) => onChange('cep', v)}
              error={erros.cep}
              inputMode="numeric"
              placeholder="00000-000"
              autoComplete="postal-code"
            />

            <CampoTexto
              id="endereco"
              name="endereco"
              label="Endereço"
              value={form.endereco}
              onChange={(v) => onChange('endereco', v)}
              error={erros.endereco}
              autoComplete="address-line1"
            />

            <CampoTexto
              id="complemento"
              name="complemento"
              label="Complemento"
              value={form.complemento}
              onChange={(v) => onChange('complemento', v)}
              error={erros.complemento}
              autoComplete="address-line2"
            />

            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              <CampoTexto
                id="bairro"
                name="bairro"
                label="Bairro"
                value={form.bairro}
                onChange={(v) => onChange('bairro', v)}
                error={erros.bairro}
              />
              <CampoTexto
                id="cidade"
                name="cidade"
                label="Cidade"
                value={form.cidade}
                onChange={(v) => onChange('cidade', v)}
                error={erros.cidade}
              />
            </div>

            <div>
              <label htmlFor="uf" className="block text-base font-medium text-foreground-950">
                Estado
              </label>
              <select
                id="uf"
                name="uf"
                value={form.uf}
                onChange={(e) => onChange('uf', e.target.value)}
                className={`mt-1 w-full border-0 border-b bg-transparent px-0 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-b-2 focus:border-primary-500 ${
                  erros.uf ? 'border-danger-500' : 'border-secondary-300'
                }`}
              >
                <option value="">Selecione</option>
                {UFS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
              {erros.uf && (
                <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                  {erros.uf}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}